"use client";

import Link from "@/components/LocalizedLink";
import { useState, type FormEvent } from "react";
import { useTranslation } from "@/lib/i18n";
import { makeTr } from "@/lib/tools-i18n";
import { INSIGHTS_BG } from "@/lib/tools-i18n/insights";

type Props = {
  slug?: string;
  variant?: "card" | "inline";
};

type Status = "idle" | "loading" | "success" | "error";

export function InsightsSubscribeForm({ slug, variant = "card" }: Props) {
  const { language } = useTranslation();
  const tr = makeTr(language, INSIGHTS_BG);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setErrorMsg("");

    try {
      const params = new URLSearchParams(window.location.search);
      const res = await fetch("/api/insights/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          slug: slug ?? null,
          utm_source: params.get("utm_source"),
          utm_campaign: params.get("utm_campaign"),
          utm_medium: params.get("utm_medium"),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setStatus("error");
        setErrorMsg(
          data?.error === "invalid_email"
            ? tr("Please enter a valid email address.")
            : tr("Something went wrong. Please try again."),
        );
        return;
      }
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
      setErrorMsg(tr("Network error. Please try again."));
    }
  }

  const isInline = variant === "inline";

  return (
    <div
      className={
        isInline
          ? "border-t border-b border-slate-200 dark:border-slate-800 py-8 my-12"
          : "rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-6 sm:p-8"
      }
    >
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2">
        {tr("Sofia Connect Insights")}
      </div>
      <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white tracking-tight mb-2">
        {tr("Weekly analysis on regional telecom infrastructure")}
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-5 leading-relaxed max-w-prose">
        {tr("DWDM, submarine cables, IP transit, peering, regulation, M&A — one email per week, written for engineers and decision-makers. No marketing.")}
      </p>

      {status === "success" ? (
        <div className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 rounded-lg px-4 py-3">
          {tr("You're subscribed. A welcome email is on its way.")}
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-2"
        >
          <label htmlFor={`insights-email-${variant}`} className="sr-only">
            {tr("Email address")}
          </label>
          <input
            id={`insights-email-${variant}`}
            type="email"
            required
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "loading"}
            className="flex-1 px-4 py-2.5 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={status === "loading" || !email}
            className="px-5 py-2.5 text-sm font-medium rounded-lg bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            {status === "loading" ? tr("Subscribing…") : tr("Subscribe")}
          </button>
        </form>
      )}

      {status === "error" && errorMsg && (
        <p className="mt-3 text-xs text-red-600 dark:text-red-400">{errorMsg}</p>
      )}

      <p className="mt-3 text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
        {tr("By subscribing you agree to receive emails from Sofia Connect EAD. You can unsubscribe at any time.")}{" "}
        <Link
          href="/legal/privacy"
          className="underline hover:text-slate-700 dark:hover:text-slate-300"
        >
          {tr("Privacy")}
        </Link>
        .
      </p>
    </div>
  );
}
